import { Tooltip, TooltipTrigger } from "@artis/ui";
import { formatProductFormula, formulaRegex, sortFormulasByMonth } from "../../../../utils/formula";
import { useMemo } from "react";
import { useAllProducts, useProductById, useProductConfigById, type TProduct } from "../../../../data";
import type { TRangeSelection } from "../../useRangeSelectionChange";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBracketRound,
  faBracketRoundRight,
  faDivide,
  faMinus,
  faPlus,
  faQuestion,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FormulaDisplay } from "./FormulaDisplay";

const chipStyle = {
  maxHeight: "20px",
  height: "20px",
  paddingLeft: "9px",
  paddingRight: "9px",
  borderRadius: "var(--webapp-radius-lg)",
  fontSize: "10.5px",
  fontWeight: "normal",
};

/**
 * Selector may contain incrementors or decrementors which specify the row index the formula is referencing.
 * An incrementor is represented by a ' and a decrementor by a ^.
 * So .value'' would reference the value two rows below the current row and .value^ would reference the value one row above the current row.
 */
function relativeRowIndexForSelector({ selector }: { selector: string }) {
  if (!selector) return null;
  if (selector.includes("'")) {
    return `-${selector.match(/'/g)?.length}`;
  }
  if (selector.includes("^")) {
    return `+${selector.match(/\^/g)?.length}`;
  }
  return null;
}

function ProductChip({
  name,
  selectorAdjacent = false,
  selector,
  fetching,
}: {
  name: string;
  selectorAdjacent?: boolean;
  selector?: string;
  fetching: boolean;
}) {
  const [productName, productPackage] = name.split(":");
  function getTitle(selector: string, productPackage: string) {
    const title = `Curve: ${productName}\n Field: ${selector?.trim().replace(/^\./, "")}\nPackage: ${productPackage}`;
    if (!productPackage) {
      return `Curve: ${productName}`;
    }
    return title;
  }

  function displayedCurveName(product: string) {
    if (fetching) return "Loading...";
    if (!productPackage) return "MISSING PERMISSION";
    const regex = /\s*\([^)]*\)/g;
    return product.replace(regex, "");
  }

  const title = getTitle(selector || "", productPackage);
  const rowIndex = relativeRowIndexForSelector({ selector: selector || "" });

  return (
    <div className="flex gap-[4.5px]">
      <TooltipTrigger placement="top">
        <div
          className="inline-flex items-center"
          style={{
            width: "auto",
            backgroundColor: productPackage || fetching ? "var(--color-formula-curve)" : "red",
            color: "var(--color-black)",
            borderTopRightRadius: selectorAdjacent ? 0 : "inherit",
            borderBottomRightRadius: selectorAdjacent ? 0 : "inherit",
            ...chipStyle,
          }}
        >
          {displayedCurveName(productName)}
        </div>
        <Tooltip>
          <span className="whitespace-pre-line">{title}</span>
        </Tooltip>
      </TooltipTrigger>
      {rowIndex ? (
        <>
          {" "}
          <RowIndexChip rowIndex={rowIndex} />
        </>
      ) : null}
    </div>
  );
}

function OperatorChip({ name }: { name: string }) {
  const icon = () => {
    if (name.includes("+")) {
      return faPlus;
    }
    if (name.includes("-")) {
      return faMinus;
    }
    if (name.includes("*")) {
      return faXmark;
    }
    if (name.includes("/")) {
      return faDivide;
    }

    // Should never happen, but just in case.
    return faQuestion;
  };

  return (
    <div className="text-[14px] font-bold mx-[4.5px] text-neutral">
      <FontAwesomeIcon color="currentcolor" icon={icon()} />
    </div>
  );
}

function Parenthesis({ name }: { name: string }) {
  return (
    <div className="text-[14px] font-bold mx-[4.5px] text-neutral">
      {name.includes("(") ? (
        <FontAwesomeIcon color="currentcolor" icon={faBracketRound} />
      ) : (
        <FontAwesomeIcon color="currentcolor" icon={faBracketRoundRight} />
      )}
    </div>
  );
}

function NumberChip({ name }: { name: string }) {
  return (
    <div
      className="inline-flex items-center"
      style={{
        backgroundColor: "var(--color-formula-value)",
        color: "var(--color-black)",
        ...chipStyle,
      }}
    >
      {name}
    </div>
  );
}

function RowIndexChip({ rowIndex }: { rowIndex?: string }) {
  if (!rowIndex) return null;
  return (
    <TooltipTrigger placement="top">
      <div
        className="inline-flex items-center"
        style={{
          ...chipStyle,
          color: "var(--color-black)",
          background: "var(--color-formula-plus)",
        }}
      >
        [{rowIndex}]
      </div>
      <Tooltip>
        Numbers in brackets indicate an offset to the currently referenced period, e.g. [-1] on M3 would reference M2
        instead.
      </Tooltip>
    </TooltipTrigger>
  );
}

export function StyledFormula({
  fetching,
  products,
  formulaString,
}: {
  fetching: boolean;
  products: TProduct[];
  formulaString: string;
}) {
  function parseReplace(formula: string) {
    const formatted = formatProductFormula(products, formula);
    if (!formatted) {
      return (
        <div className="flex font-normal text-xs leading-[18px] items-center gap-2 text-foreground">
          Value: {formula}
        </div>
      );
    }
    const parts = formatted.split(formulaRegex).flatMap((part, index) => {
      return index % 2 === 1 ? part : part?.trim().split(/(\+|-|\*|\/|\(|\))/);
    });

    const styled = parts.reduce<React.ReactNode[]>((acc, part, index) => {
      const key = part + index;
      if (part?.trim()?.match(/^-?\d+\.?\d*$/)) {
        acc.push(<NumberChip key={key} name={part} />);
        return acc;
      }
      if (part === "MANUAL") {
        acc.push(
          <div
            key={key}
            className="inline-flex items-center"
            style={{
              ...chipStyle,
              backgroundColor: "var(--color-neutral)",
              color: "var(--color-black)",
            }}
          >
            MANUAL
          </div>,
        );
        return acc;
      }

      if (part?.length === 1) {
        if (["+", "-", "*", "/"].includes(part)) {
          acc.push(<OperatorChip key={key} name={part} />);
          return acc;
        }

        if (["(", ")"].includes(part)) {
          acc.push(<Parenthesis key={key} name={part} />);
          return acc;
        }
      }

      if (index % 2 === 1) {
        const nextPart = parts?.[index + 1];
        acc.push(
          <ProductChip
            key={key}
            fetching={fetching}
            name={part}
            selectorAdjacent={nextPart?.charAt(0) === "."}
            selector={nextPart?.charAt(0) === "." ? nextPart : undefined}
          />,
        );
      } else if (part !== "" && part.charAt(0) !== ".") {
        acc.push(
          <div
            className="flex items-center justify-center -mt-[1.5px] text-foreground"
            style={{
              ...chipStyle,
              fontWeight: "bold",
            }}
          >
            {part}
          </div>,
        );
      }

      return acc;
    }, []);

    return (
      <div className="flex font-normal text-xs leading-[18px] items-center gap-2 text-foreground">
        {styled}
        <div className="min-w-2 min-h-2" />
      </div>
    );
  }

  return parseReplace(formulaString);
}

export function FormulaViewer({ selection }: { selection: TRangeSelection }) {
  const ranges = selection;
  const params = ranges.flat().map((cell) => {
    return {
      index: cell.rowIndex,
      productId: cell.productId,
      headerParams: { name: cell.headerName, columnId: cell.columnId },
      value: 29,
      columnId: cell.columnId,
    };
  });

  const latestCell = params[params.length - 1];
  const latestProductId = latestCell?.productId || "";
  const { data, isFetching: fetching } = useAllProducts();
  const { data: selectedProduct } = useProductById(latestProductId);
  const configs = useProductConfigById(latestProductId);

  const sortedFormulas = useMemo(() => sortFormulasByMonth(configs.data ?? []), [configs.data]) || [];

  return (
    <div className="h-full px-6">
      {fetching || !selectedProduct ? (
        <div className="p-4">
          <p className="text-lg text-foreground">
            {`${selectedProduct?.code}${
              selectedProduct?.description && selectedProduct?.description !== selectedProduct?.code
                ? ` (${selectedProduct?.description})`
                : ""
            }`}
          </p>
          <div className="flex flex-col gap-1">
            {sortedFormulas.map(({ formula, month }) => (
              <div
                key={`${formula}-${month}`}
                className="animate-pulse bg-card-muted h-12 rounded-sm last:mb-0 mb-[5px]"
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="h-full overflow-auto grid [grid-template-rows:1fr_min-content] max-w-3xl pr-6">
          <div>
            <div className="flex items-center gap-1 mb-6">
              <p className="text-base text-foreground">Selected curve:</p>
              <p className="text-base text-foreground-muted">
                {`${selectedProduct?.code}${
                  selectedProduct?.description && selectedProduct?.description !== selectedProduct?.code
                    ? ` (${selectedProduct?.description})`
                    : ""
                }`}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              {sortedFormulas.map(({ formula, month }) => (
                <div key={`${formula}-${month}`}>
                  <p className="text-base font-medium text-foreground">{month}:</p>
                  <div key={`${formula}-${month}`} className="flex flex-wrap bg-card-muted rounded-lg mt-1 px-3 py-2">
                    <FormulaDisplay
                      fetching={fetching}
                      products={Object.values(data ?? {})}
                      formulaString={formula ?? "MANUAL"}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
